import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, ChevronRight, Check, Dumbbell, Brain, Apple, Moon, Wine, Users, Cigarette, Pill, FlaskConical, Shield, Zap, AlertTriangle, Mail, Loader2, TrendingUp, History, Save, LogIn } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar, Legend } from 'recharts';
import ShareResult from '../components/ShareResult';
import { leadsApi, longevityApi } from '../lib/api';
import { useAuth } from '../lib/AuthContext';

/* ─── Assessment data ─── */
const CATEGORIES = [
  { id: 'exercise', label: 'Physical Activity', icon: Dumbbell, color: '#c8ff00', years: 4.5,
    questions: [
      { q: 'How many days per week do you exercise?', opts: ['0 days', '1-2 days', '3-4 days', '5+ days'] },
      { q: 'Average session intensity?', opts: ['None / sedentary', 'Light (walking)', 'Moderate (jogging, weights)', 'Vigorous (HYROX, HIIT, heavy lifts)'] },
      { q: 'Can you run 5K without stopping?', opts: ['No', 'Barely / walk breaks', 'Yes, comfortably', 'Under 25 minutes'] },
      { q: 'How long have you been training consistently?', opts: ['Not training', '< 6 months', '6-12 months', '1+ years'] },
    ],
    gapAdvice: 'Start with 150 min/week of moderate movement. Build to 3-5 sessions mixing cardio and strength.',
    peptide: { name: 'BPC-157', reason: 'Supports joint resilience, tendon healing, and gut integrity — letting you train harder for longer.', link: 'https://www.americanpeptideresearch.net/product/bpc-157-10mg/ref/16/' },
  },
  { id: 'smoking', label: 'No Smoking', icon: Cigarette, color: '#E63946', years: 4.5,
    questions: [
      { q: 'Current smoking status?', opts: ['Daily smoker', 'Occasional / social', 'Quit < 1 year ago', 'Quit 1+ year / never smoked'] },
      { q: 'Secondhand smoke exposure?', opts: ['Daily', 'Several times/week', 'Rarely', 'Never'] },
      { q: 'Vaping or nicotine products?', opts: ['Daily', 'Occasionally', 'Rarely', 'None'] },
    ],
    gapAdvice: 'Quitting is the single highest-ROI health decision. GHK-Cu supports lung tissue repair post-cessation.',
    peptide: { name: 'GHK-Cu', reason: 'Copper peptide supporting lung tissue regeneration, collagen rebuilding, and oxidative damage repair.', link: '/blogs/article/the-power-of-methylation-health-and-wellness-from-within' },
  },
  { id: 'stress', label: 'Stress Management', icon: Brain, color: '#818cf8', years: 3.5,
    questions: [
      { q: 'Do you practice meditation, breathwork, or mindfulness?', opts: ['Never', 'Rarely (monthly)', 'Sometimes (weekly)', 'Daily practice'] },
      { q: 'Active recovery days per week?', opts: ['0', '1', '2', '3+'] },
      { q: 'Do you maintain work-life boundaries?', opts: ['Never — always on', 'Sometimes', 'Usually', 'Strong boundaries'] },
      { q: 'How often do you wake up feeling genuinely rested?', opts: ['Rarely', 'Sometimes', 'Most days', 'Almost always'] },
    ],
    gapAdvice: 'Add a daily 10-min breathwork or meditation practice. Schedule 2 active recovery days/week.',
    peptide: { name: 'Dihexa', reason: 'Nootropic peptide supporting neuroplasticity and neuroprotection against stress-induced cognitive decline.', link: '/blogs/article/dihexa-peptide-brain-repair-what-the-science-actually-says' },
  },
  { id: 'nutrition', label: 'Quality Nutrition', icon: Apple, color: '#22c55e', years: 2,
    questions: [
      { q: 'Daily protein intake relative to bodyweight?', opts: ['Very low (< 0.5g/lb)', 'Low (0.5-0.7g/lb)', 'Adequate (0.7-1g/lb)', 'Optimized (1-1.2g/lb)'] },
      { q: 'Fruit & vegetable servings per day?', opts: ['0-1', '2-3', '4-5', '6+'] },
      { q: 'Ultra-processed food in your diet?', opts: ['Most meals (50%+)', 'Frequent (25-50%)', 'Occasional (10-25%)', 'Minimal (< 10%)'] },
      { q: 'Daily water intake?', opts: ['< 4 glasses', '4-6 glasses', '7-9 glasses', '10+ glasses'] },
    ],
    gapAdvice: 'Prioritize 1g protein/lb bodyweight, 5+ servings of fruits and vegetables, and minimize ultra-processed foods.',
    peptide: { name: 'MOTS-c', reason: 'Mitochondrial peptide that activates AMPK, improves insulin sensitivity, and mimics caloric restriction benefits.', link: 'https://www.americanpeptideresearch.net/product/mots-c-10mg/ref/16/' },
  },
  { id: 'opioids', label: 'No Opioid Dependency', icon: Pill, color: '#f59e0b', years: 4.5,
    questions: [
      { q: 'How do you manage chronic or acute pain?', opts: ['Depend on opioid prescriptions', 'Occasional opioid use', 'Natural alternatives mostly', 'No chronic pain / fully alternative'] },
      { q: 'Alternative recovery modalities?', opts: ['None', 'Basic (ice, stretching)', 'Good (physio, mobility work)', 'Comprehensive (peptides, cold therapy, etc.)'] },
      { q: 'Anti-inflammatory supplement stack?', opts: ['None', 'Basic (fish oil)', 'Good (fish oil, curcumin, etc.)', 'Optimized protocol'] },
    ],
    gapAdvice: 'Explore regenerative alternatives: BPC-157 for tissue repair, hydrotherapy, cold plunge, and structured mobility work.',
    peptide: { name: 'BPC-157', reason: 'Body Protection Compound for musculoskeletal repair, tendon healing, and genuine tissue regeneration.', link: 'https://www.americanpeptideresearch.net/product/bpc-157-10mg/ref/16/' },
  },
  { id: 'alcohol', label: 'Minimal Alcohol', icon: Wine, color: '#a78bfa', years: 3,
    questions: [
      { q: 'How often do you drink?', opts: ['Daily', '3-5x per week', '1-2x per week', 'Rarely or never'] },
      { q: 'Drinks per occasion?', opts: ['4+', '2-3', '1', '0'] },
      { q: 'Does alcohol affect your training or recovery?', opts: ['Frequently', 'Sometimes', 'Rarely', 'Never / N/A'] },
    ],
    gapAdvice: 'No amount is "health-promoting." Cut to < 4 drinks/week. Eliminate alcohol within 3 hours of sleep.',
    peptide: { name: 'TB-500', reason: 'Thymosin Beta-4 supports systemic inflammation control and tissue repair from cumulative alcohol damage.', link: 'https://www.americanpeptideresearch.net/product/tb-500-10mg/ref/16/' },
  },
  { id: 'sleep', label: 'Quality Sleep', icon: Moon, color: '#00F0FF', years: 4.5,
    questions: [
      { q: 'Average hours of sleep per night?', opts: ['< 6 hours', '6-7 hours', '7-8 hours', '8-9 hours'] },
      { q: 'Sleep/wake time consistency?', opts: ['Varies wildly', 'Somewhat consistent', 'Usually consistent (within 30 min)', 'Rock solid every day'] },
      { q: 'Sleep environment (dark, cool, quiet)?', opts: ['Poor', 'OK', 'Good', 'Fully optimized'] },
      { q: 'Screen-free time before bed?', opts: ['Use phone in bed', '30 min before', '60 min before', '90+ min before'] },
    ],
    gapAdvice: 'Target 7-9 hours with ±30min consistency. Cool room (65-68F), blackout curtains, no blue light 90 min pre-sleep.',
    peptide: { name: 'MK-677', reason: 'Growth hormone secretagogue that amplifies deep-sleep GH pulsing for cellular repair and recovery.', link: 'https://www.americanpeptideresearch.net/ref/16/' },
  },
  { id: 'social', label: 'Social Connection', icon: Users, color: '#ec4899', years: 3.5,
    questions: [
      { q: 'Are you part of a fitness or training community?', opts: ['No', 'Online only', 'Local group', 'Active, consistent member'] },
      { q: 'Close, supportive relationships in your life?', opts: ['Isolated', 'A few', 'Good network', 'Strong, deep bonds'] },
      { q: 'Do you have a training partner or accountability group?', opts: ['Always solo', 'Occasionally', 'Regular partner', 'Dedicated group'] },
      { q: 'How often do you have meaningful social time?', opts: ['Rarely', 'Monthly', 'Weekly', 'Several times/week'] },
    ],
    gapAdvice: 'Join a training community (run club, HYROX gym, CrossFit). Social connection rivals exercise for longevity impact.',
    peptide: null, // No peptide for this - it's community
    communityLink: true,
  },
];

const MAX_SCORE = CATEGORIES.reduce((s, c) => s + c.questions.length * 3, 0);

const TIERS = [
  { min: 0, pct: 0.25, label: 'Starting Point', color: '#71717a', desc: 'You have significant room for improvement — but that means every change you make will have massive impact.' },
  { min: 0.25, pct: 0.5, label: 'Building Foundation', color: '#f59e0b', desc: 'You\'ve got some habits dialed in. Focus on your weakest categories — that\'s where the biggest gains live.' },
  { min: 0.5, pct: 0.75, label: 'High Performer', color: '#00F0FF', desc: 'Solid across the board. Time to optimize your remaining gaps with precision tools and peptide protocols.' },
  { min: 0.75, pct: 1.01, label: 'Longevity Athlete', color: '#c8ff00', desc: 'Elite-level habits. You\'re ready for full biomarker tracking and precision peptide optimization.' },
];

export default function LongevityScorePage() {
  const { user, token } = useAuth();
  const [step, setStep] = useState(0); // 0 = intro, 1-8 = categories, 9 = results
  const [answers, setAnswers] = useState({});
  const [emailForm, setEmailForm] = useState({ email: '', first_name: '' });
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [scoreSaved, setScoreSaved] = useState(false);
  const [scoreSaving, setScoreSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const topRef = useRef(null);
  const hasSavedRef = useRef(false);

  const cat = step >= 1 && step <= 8 ? CATEGORIES[step - 1] : null;
  const totalCats = CATEGORIES.length;

  // Scoring
  const getCatScore = useCallback((catId) => {
    const c = CATEGORIES.find(x => x.id === catId);
    if (!c) return 0;
    return c.questions.reduce((s, _, qi) => s + (answers[`${catId}_${qi}`] || 0), 0);
  }, [answers]);
  const getCatMax = useCallback((catId) => {
    const c = CATEGORIES.find(x => x.id === catId);
    return c ? c.questions.length * 3 : 1;
  }, []);
  const totalScore = CATEGORIES.reduce((s, c) => s + getCatScore(c.id), 0);
  const pct = totalScore / MAX_SCORE;
  const tier = TIERS.find(t => pct >= t.min && pct < t.pct) || TIERS[0];

  // Gap analysis: categories under 50% score
  const gaps = CATEGORIES
    .map(c => ({ ...c, score: getCatScore(c.id), max: getCatMax(c.id), pct: getCatScore(c.id) / getCatMax(c.id) }))
    .sort((a, b) => a.pct - b.pct);
  const weakGaps = gaps.filter(g => g.pct < 0.6);
  const estYearsAdded = CATEGORIES.reduce((s, c) => {
    const catPct = getCatScore(c.id) / getCatMax(c.id);
    return s + (c.years * catPct);
  }, 0);

  // Fetch history on mount if authenticated
  const fetchHistory = useCallback(async () => {
    if (!token) return;
    setHistoryLoading(true);
    try {
      const res = await longevityApi.getHistory(token);
      setHistory(res.data.scores || []);
    } catch (e) { console.error('Failed to load longevity history', e); }
    finally { setHistoryLoading(false); }
  }, [token]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Auto-save score when reaching results
  const saveScore = useCallback(async () => {
    if (!token || hasSavedRef.current) return;
    hasSavedRef.current = true;
    setScoreSaving(true);
    try {
      const habitsPayload = CATEGORIES.map(c => ({
        id: c.id, label: c.label, score: getCatScore(c.id), max: getCatMax(c.id),
        pct: parseFloat((getCatScore(c.id) / getCatMax(c.id)).toFixed(3)),
      }));
      await longevityApi.saveScore({
        total_score: totalScore, max_score: MAX_SCORE, tier: tier.label,
        est_years: parseFloat(estYearsAdded.toFixed(1)), habits: habitsPayload,
      }, token);
      setScoreSaved(true);
      fetchHistory();
    } catch (e) { console.error('Failed to save longevity score', e); }
    finally { setScoreSaving(false); }
  }, [token, totalScore, tier, estYearsAdded, fetchHistory, getCatScore, getCatMax]);

  useEffect(() => {
    if (step === CATEGORIES.length + 1 && token && !hasSavedRef.current) {
      saveScore();
    }
  }, [step, token, saveScore]);

  const canProceed = () => {
    if (!cat) return true;
    return cat.questions.every((_, qi) => answers[`${cat.id}_${qi}`] !== undefined);
  };

  const goNext = () => {
    if (step < totalCats + 1) {
      setStep(s => s + 1);
      topRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  };
  const goBack = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const selectAnswer = (catId, qi, score) => {
    setAnswers(prev => ({ ...prev, [`${catId}_${qi}`]: score }));
  };

  const restart = () => { setStep(0); setAnswers({}); setEmailSent(false); setEmailError(''); setEmailForm({ email: '', first_name: '' }); setScoreSaved(false); hasSavedRef.current = false; setShowHistory(false); };

  const sendReport = async () => {
    if (!emailForm.email) return;
    setEmailSending(true);
    setEmailError('');
    try {
      const habitsPayload = CATEGORIES.map(c => ({
        id: c.id, label: c.label, score: getCatScore(c.id), max: getCatMax(c.id),
        color: c.color, pct: getCatScore(c.id) / getCatMax(c.id),
        gapAdvice: c.gapAdvice,
        peptide: c.peptide ? { name: c.peptide.name, reason: c.peptide.reason } : null,
      }));
      await leadsApi.sendLongevityReport({
        email: emailForm.email,
        first_name: emailForm.first_name,
        total_score: totalScore,
        max_score: MAX_SCORE,
        tier: tier.label,
        est_years: parseFloat(estYearsAdded.toFixed(1)),
        habits: habitsPayload,
      });
      setEmailSent(true);
    } catch {
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setEmailSending(false);
    }
  };

  return (
    <main className="pt-20 min-h-screen bg-[#0a0a0a]" data-testid="longevity-score-page" ref={topRef}>
      <div className="max-w-3xl mx-auto px-6 py-12">

        {/* ─── INTRO ─── */}
        {step === 0 && (
          <div data-testid="longevity-intro">
            <Link to="/tools" className="text-zinc-500 text-xs uppercase tracking-wider hover:text-white transition-colors mb-6 inline-block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Back to Tools</Link>

            <p className="font-mono text-[11px] text-[#c8ff00] uppercase tracking-[2px] mb-4 flex items-center gap-2.5">
              <span className="inline-block w-6 h-px bg-[#c8ff00]" />Longevity Assessment
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-white leading-tight mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Your Longevity Score
            </h1>
            <p className="text-zinc-400 text-lg leading-relaxed mb-6 max-w-xl">
              A deep assessment across the 8 habits scientifically proven to add up to <strong className="text-white">24 years</strong> to your lifespan. Get your per-habit breakdown and personalized peptide recommendations.
            </p>

            {/* What you'll get */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-10">
              {[
                { icon: Shield, label: 'Per-habit scores', desc: '8 categories, 29 questions', color: '#c8ff00' },
                { icon: AlertTriangle, label: 'Gap analysis', desc: 'Weakest links identified', color: '#E63946' },
                { icon: FlaskConical, label: 'Peptide Rx', desc: 'Targeted recommendations', color: '#00F0FF' },
              ].map(f => (
                <div key={f.label} className="border border-zinc-800 p-4 bg-zinc-900/30">
                  <f.icon className="w-5 h-5 mb-2" style={{ color: f.color }} />
                  <p className="text-white text-sm font-semibold">{f.label}</p>
                  <p className="text-zinc-600 text-xs">{f.desc}</p>
                </div>
              ))}
            </div>

            <button onClick={goNext}
              className="px-10 py-4 bg-[#c8ff00] text-black font-bold text-sm uppercase tracking-wider hover:bg-white transition-colors"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
              data-testid="start-assessment-btn">
              {history.length > 0 ? 'Retake Assessment' : 'Start Assessment'} <ArrowRight className="w-4 h-4 inline ml-1" />
            </button>
            {token && history.length > 0 && (
              <p className="text-zinc-600 text-xs mt-3">
                <History className="w-3 h-3 inline mr-1" />
                You've completed {history.length} assessment{history.length !== 1 ? 's' : ''}. Your latest score: <strong className="text-[#c8ff00]">{history[0]?.total_score}/{history[0]?.max_score}</strong>
              </p>
            )}
          </div>
        )}

        {/* ─── CATEGORY QUESTIONS ─── */}
        {cat && (
          <div data-testid={`category-${cat.id}`}>
            {/* Progress */}
            <div className="flex items-center gap-2 mb-6">
              {CATEGORIES.map((c, i) => (
                // eslint-disable-next-line no-nested-ternary -- readable tier picker
                <div key={c.id} className={`flex-1 h-1.5 transition-all ${i < step ? 'bg-[#c8ff00]' : i === step - 1 ? 'bg-[#c8ff00]/50' : 'bg-zinc-800'}`} />
              ))}
            </div>

            <div className="flex items-center gap-3 mb-2">
              <cat.icon className="w-6 h-6" style={{ color: cat.color }} />
              <span className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider">{step} of {totalCats}</span>
            </div>
            <h2 className="text-3xl text-white mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{cat.label}</h2>
            <p className="text-zinc-500 text-xs mb-8">Contributes up to <strong className="text-zinc-300">+{cat.years} years</strong> to lifespan</p>

            <div className="space-y-5">
              {cat.questions.map((qObj, qi) => (
                <div key={qObj.q} data-testid={`question-${cat.id}-${qi}`}>
                  <p className="text-zinc-300 text-sm mb-3">{qObj.q}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {qObj.opts.map((opt, oi) => {
                      const selected = answers[`${cat.id}_${qi}`] === oi;
                      return (
                        <button key={opt}
                          onClick={() => selectAnswer(cat.id, qi, oi)}
                          className={`text-left px-4 py-3 border text-sm transition-all ${selected ? 'border-white/40 bg-white/5 text-white' : 'border-zinc-800 text-zinc-500 hover:border-zinc-700 hover:text-zinc-300'}`}
                          style={selected ? { borderColor: `${cat.color}50`, background: `${cat.color}08` } : {}}
                          data-testid={`opt-${cat.id}-${qi}-${oi}`}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center justify-between mt-10 pt-6 border-t border-zinc-800">
              <button onClick={goBack} className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider hover:text-white transition-colors" data-testid="back-btn">
                <ArrowLeft className="w-3.5 h-3.5" /> Back
              </button>
              <button onClick={goNext}
                disabled={!canProceed()}
                className={`px-8 py-3 text-sm uppercase tracking-wider font-bold transition-all ${canProceed() ? 'bg-[#c8ff00] text-black hover:bg-white' : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'}`}
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                data-testid="next-btn">
                {step === totalCats ? 'See Results' : 'Next'} <ChevronRight className="w-4 h-4 inline ml-1" />
              </button>
            </div>
          </div>
        )}

        {/* ─── RESULTS ─── */}
        {step === totalCats + 1 && (
          <div data-testid="longevity-results">
            <Link to="/tools" className="text-zinc-500 text-xs uppercase tracking-wider hover:text-white transition-colors mb-6 inline-block" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Back to Tools</Link>

            {/* Score headline */}
            <div className="text-center mb-10">
              <p className="font-mono text-[11px] text-zinc-600 uppercase tracking-[2px] mb-3">Your Longevity Score</p>
              <div className="flex items-end justify-center gap-1">
                <span className="text-7xl sm:text-8xl leading-none" style={{ fontFamily: 'Bebas Neue, sans-serif', color: tier.color }}>{totalScore}</span>
                <span className="text-3xl text-zinc-700 mb-1" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>/{MAX_SCORE}</span>
              </div>
              <p className="text-xl mt-3 mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif', color: tier.color }}>{tier.label}</p>
              <p className="text-zinc-400 text-sm max-w-md mx-auto">{tier.desc}</p>
            </div>

            {/* Est years */}
            <div className="bg-zinc-900/60 border border-zinc-800 p-5 text-center mb-10">
              <p className="text-[10px] text-zinc-600 font-mono uppercase tracking-wider mb-1">Estimated Years Added by Current Habits</p>
              <p className="text-4xl" style={{ fontFamily: 'Bebas Neue, sans-serif', color: '#c8ff00' }}>+{estYearsAdded.toFixed(1)} years</p>
              <p className="text-zinc-600 text-xs mt-1">of a possible +30 years (compounding effects)</p>
            </div>

            {/* Save status indicator */}
            {token && (
              <div className="flex items-center gap-2 mb-4 text-xs" data-testid="save-status">
                {scoreSaving && <><Loader2 className="w-3.5 h-3.5 animate-spin text-[#c8ff00]" /><span className="text-zinc-500">Saving your score...</span></>}
                {scoreSaved && <><Save className="w-3.5 h-3.5 text-[#c8ff00]" /><span className="text-zinc-500">Score saved to your profile</span></>}
              </div>
            )}
            {!token && (
              <div className="border border-zinc-800 bg-zinc-900/40 p-4 mb-6 flex items-center gap-3" data-testid="login-prompt">
                <LogIn className="w-5 h-5 text-[#c8ff00] flex-shrink-0" />
                <div>
                  <p className="text-zinc-300 text-sm">Want to track your progress over time?</p>
                  <Link to="/login" className="text-[#c8ff00] text-xs font-semibold hover:underline">Log in or sign up</Link>
                  <span className="text-zinc-600 text-xs"> to save scores and see trends.</span>
                </div>
              </div>
            )}

            {/* Score History Section */}
            {token && history.length > 1 && (
              <div className="mb-10" data-testid="score-history-section">
                <button onClick={() => setShowHistory(h => !h)}
                  className="flex items-center gap-2 text-white text-sm mb-4 hover:text-[#c8ff00] transition-colors"
                  data-testid="toggle-history-btn">
                  <History className="w-4 h-4" />
                  <span style={{ fontFamily: 'Bebas Neue, sans-serif' }} className="text-xl">Your Progress Over Time</span>
                  <TrendingUp className="w-4 h-4 text-[#c8ff00]" />
                </button>

                {showHistory && (
                  <div className="space-y-6">
                    {/* Total Score Line Chart */}
                    <div className="border border-zinc-800 bg-zinc-900/30 p-5">
                      <p className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider mb-4">Total Score Trend</p>
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={[...history].reverse().map(h => ({
                          date: new Date(h.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
                          score: h.total_score,
                          years: h.est_years,
                        }))}>
                          <XAxis dataKey="date" stroke="#52525b" tick={{ fill: '#71717a', fontSize: 11 }} />
                          <YAxis stroke="#52525b" tick={{ fill: '#71717a', fontSize: 11 }} domain={[0, MAX_SCORE]} />
                          <Tooltip
                            contentStyle={{ background: '#18181b', border: '1px solid #27272a', borderRadius: 0, fontSize: 12 }}
                            labelStyle={{ color: '#a1a1aa' }}
                            itemStyle={{ color: '#c8ff00' }}
                          />
                          <Line type="monotone" dataKey="score" stroke="#c8ff00" strokeWidth={2} dot={{ r: 4, fill: '#c8ff00' }} name="Score" />
                          <Line type="monotone" dataKey="years" stroke="#00F0FF" strokeWidth={1.5} dot={{ r: 3, fill: '#00F0FF' }} name="Est. Years" />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Habit Radar — latest vs first */}
                    {history.length >= 2 && history[0].habits && history[history.length - 1].habits && (
                      <div className="border border-zinc-800 bg-zinc-900/30 p-5">
                        <p className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider mb-1">Habit Improvement</p>
                        <p className="text-zinc-700 text-[10px] mb-4">Latest assessment vs your first — see where you've grown.</p>
                        <ResponsiveContainer width="100%" height={280}>
                          <RadarChart data={
                            (history[0].habits || []).map(h => {
                              const firstH = (history[history.length - 1].habits || []).find(f => f.id === h.id);
                              return {
                                habit: h.label.replace('No ', '').replace('Quality ', '').replace('Minimal ', ''),
                                Latest: Math.round((h.pct || 0) * 100),
                                First: Math.round((firstH?.pct || 0) * 100),
                              };
                            })
                          }>
                            <PolarGrid stroke="#27272a" />
                            <PolarAngleAxis dataKey="habit" tick={{ fill: '#a1a1aa', fontSize: 10 }} />
                            <Radar name="First" dataKey="First" stroke="#71717a" fill="#71717a" fillOpacity={0.15} />
                            <Radar name="Latest" dataKey="Latest" stroke="#c8ff00" fill="#c8ff00" fillOpacity={0.2} />
                            <Legend wrapperStyle={{ fontSize: 11, color: '#a1a1aa' }} />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Score delta summary */}
                    {history.length >= 2 && (
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="score-deltas">
                        {(() => {
                          const latest = history[0];
                          const prev = history[1];
                          const scoreDelta = latest.total_score - prev.total_score;
                          const yearsDelta = (latest.est_years || 0) - (prev.est_years || 0);
                          return [
                            { label: 'Score Change', val: `${scoreDelta >= 0 ? '+' : ''}${scoreDelta}`, color: scoreDelta >= 0 ? '#c8ff00' : '#E63946' },
                            { label: 'Years Change', val: `${yearsDelta >= 0 ? '+' : ''}${yearsDelta.toFixed(1)}`, color: yearsDelta >= 0 ? '#22c55e' : '#E63946' },
                            { label: 'Assessments', val: history.length, color: '#00F0FF' },
                            { label: 'Latest Tier', val: latest.tier, color: TIERS.find(t => t.label === latest.tier)?.color || '#fff' },
                          ].map(s => (
                            <div key={s.label} className="border border-zinc-800 p-3 text-center">
                              <p className="text-[10px] font-mono text-zinc-600 uppercase tracking-wider">{s.label}</p>
                              <p className="text-xl mt-1" style={{ fontFamily: 'Bebas Neue, sans-serif', color: s.color }}>{s.val}</p>
                            </div>
                          ));
                        })()}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Per-habit breakdown */}
            <h3 className="text-xl text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Habit Breakdown</h3>
            <div className="space-y-3 mb-10" data-testid="habit-breakdown">
              {gaps.map(g => {
                const CatIcon = g.icon;
                const scorePct = g.max > 0 ? (g.score / g.max) * 100 : 0;
                return (
                  <div key={g.id} className="border border-zinc-800 p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <CatIcon className="w-4 h-4" style={{ color: g.color }} />
                        <span className="text-white text-sm font-semibold">{g.label}</span>
                      </div>
                      <span className="text-sm font-mono" style={{ color: scorePct >= 60 ? g.color : '#E63946' }}>{g.score}/{g.max}</span>
                    </div>
                    <div className="w-full h-2 bg-zinc-800 overflow-hidden">
                      {/* eslint-disable-next-line no-nested-ternary -- readable tier picker */}
                      <div className="h-full transition-all duration-500" style={{ width: `${scorePct}%`, background: scorePct >= 75 ? g.color : scorePct >= 50 ? '#f59e0b' : '#E63946' }} />
                    </div>
                    {scorePct < 60 && (
                      <p className="text-zinc-600 text-xs mt-2 flex items-start gap-1.5">
                        <AlertTriangle className="w-3 h-3 text-[#E63946] flex-shrink-0 mt-0.5" />
                        <span>{g.gapAdvice}</span>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Peptide recommendations for gaps */}
            {weakGaps.length > 0 && (
              <div className="mb-10" data-testid="peptide-recommendations">
                <h3 className="text-xl text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Your Personalized Peptide Protocol</h3>
                <p className="text-zinc-500 text-xs mb-5">Targeted recommendations based on your weakest categories.</p>
                <div className="space-y-3">
                  {weakGaps.map(g => {
                    if (g.communityLink) {
                      return (
                        <div key={g.id} className="border border-zinc-800 bg-zinc-900/40 p-5">
                          <div className="flex items-center gap-2 mb-2">
                            <Users className="w-4 h-4 text-[#ec4899]" />
                            <span className="font-mono text-[10px] text-[#ec4899] uppercase tracking-wider">Community</span>
                          </div>
                          <p className="text-white text-sm font-semibold mb-1">Join The Dojo</p>
                          <p className="text-zinc-500 text-xs leading-relaxed mb-3">Social connection rivals exercise for longevity impact. No supplement can replace human bonds.</p>
                          <Link to="/community" className="text-[10px] font-bold text-white uppercase tracking-wider border-b-2 border-[#ec4899] pb-0.5 hover:text-[#ec4899] transition-colors" data-testid="join-dojo-link">
                            Join The Dojo <ArrowRight className="w-3 h-3 inline" />
                          </Link>
                        </div>
                      );
                    }
                    if (!g.peptide) return null;
                    return (
                      <div key={g.id} className="border border-zinc-800 bg-zinc-900/40 p-5">
                        <div className="flex items-center gap-2 mb-2">
                          <FlaskConical className="w-4 h-4" style={{ color: g.color }} />
                          <span className="font-mono text-[10px] uppercase tracking-wider" style={{ color: g.color }}>For: {g.label}</span>
                        </div>
                        <p className="text-white text-sm font-semibold mb-1">{g.peptide.name}</p>
                        <p className="text-zinc-500 text-xs leading-relaxed mb-3">{g.peptide.reason}</p>
                        <a href={g.peptide.link} target={g.peptide.link.startsWith('/') ? undefined : '_blank'} rel="noopener noreferrer"
                          className="text-[10px] font-bold text-white uppercase tracking-wider border-b-2 border-[#c8ff00] pb-0.5 hover:text-[#c8ff00] transition-colors no-underline"
                          data-testid={`peptide-rec-${g.id}`}>
                          {g.peptide.link.startsWith('/') ? 'Read Guide' : `Shop ${g.peptide.name}`} <ArrowRight className="w-3 h-3 inline" />
                        </a>
                      </div>
                    );
                  })}
                </div>

                <div className="border-l-4 border-zinc-700 bg-zinc-900/50 p-4 mt-5">
                  <p className="text-zinc-600 text-xs leading-relaxed">Peptides are for research purposes. Always consult a physician before beginning any protocol. Start with <Link to="/blogs/article/blood-tests-before-peptides-the-complete-pre-protocol-lab-guide" className="text-[#c8ff00] hover:underline">baseline blood work</Link>.</p>
                </div>
              </div>
            )}

            {/* Email Capture */}
            <div className="border border-[#c8ff00]/30 bg-[#c8ff00]/5 p-6 mb-10" data-testid="email-capture">
              {!emailSent ? (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-5 h-5 text-[#c8ff00]" />
                    <h3 className="text-xl text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Get Your Full Report</h3>
                  </div>
                  <p className="text-zinc-500 text-xs mb-5">Your personalized longevity protocol — habit breakdown, gap analysis, and peptide recommendations — delivered to your inbox.</p>
                  <div className="flex flex-col sm:flex-row gap-2 mb-3">
                    <input type="text" placeholder="First name"
                      value={emailForm.first_name}
                      onChange={e => setEmailForm(f => ({ ...f, first_name: e.target.value }))}
                      className="sm:w-36 bg-zinc-900 border border-zinc-800 text-white text-sm p-3 outline-none focus:border-[#c8ff00] placeholder-zinc-600"
                      data-testid="report-first-name" />
                    <input type="email" placeholder="Your email"
                      value={emailForm.email}
                      onChange={e => setEmailForm(f => ({ ...f, email: e.target.value }))}
                      className="flex-1 bg-zinc-900 border border-zinc-800 text-white text-sm p-3 outline-none focus:border-[#c8ff00] placeholder-zinc-600"
                      data-testid="report-email" />
                    <button onClick={sendReport}
                      disabled={!emailForm.email || emailSending}
                      className={`px-6 py-3 text-sm font-bold uppercase tracking-wider transition-all flex items-center gap-2 justify-center ${emailForm.email && !emailSending ? 'bg-[#c8ff00] text-black hover:bg-white' : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'}`}
                      style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                      data-testid="send-report-btn">
                      {emailSending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending...</> : <>Send My Report <ArrowRight className="w-4 h-4" /></>}
                    </button>
                  </div>
                  {emailError && <p className="text-red-500 text-xs">{emailError}</p>}
                  <p className="text-zinc-700 text-[10px]">No spam. Just your results. Unsubscribe anytime.</p>
                </>
              ) : (
                <div className="text-center py-4" data-testid="email-success">
                  <Check className="w-8 h-8 text-[#c8ff00] mx-auto mb-3" />
                  <h3 className="text-xl text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Report Sent!</h3>
                  <p className="text-zinc-400 text-sm">Check your inbox for your personalized longevity protocol. It includes your full habit breakdown, gap analysis, and targeted peptide recommendations.</p>
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap items-center gap-3 mb-10">
              <ShareResult
                text={`My Longevity Score: ${totalScore}/${MAX_SCORE} — ${tier.label}! Estimated +${estYearsAdded.toFixed(1)} years from current habits. Take the full assessment:`}
                url="https://ninjathlete.com/tools/longevity-score"
                label="Share Score"
              />
              <button onClick={restart}
                className="px-4 py-2 border border-zinc-700 text-zinc-400 text-xs uppercase tracking-wider hover:text-white transition-colors"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                data-testid="retake-btn">Retake Assessment</button>
              <Link to="/blogs/article/the-longevity-blueprint"
                className="px-4 py-2 border border-[#c8ff00]/30 text-[#c8ff00] text-xs uppercase tracking-wider hover:bg-[#c8ff00]/10 transition-colors no-underline"
                style={{ fontFamily: 'Bebas Neue, sans-serif' }}
                data-testid="read-blueprint-link">Read The Full Blueprint <ArrowRight className="w-3 h-3 inline ml-1" /></Link>
            </div>

            {/* CTA */}
            <div className="bg-zinc-900 border border-zinc-800 p-8 text-center">
              <FlaskConical className="w-8 h-8 text-[#c8ff00] mx-auto mb-3" />
              <h3 className="text-2xl text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>Ready for Precision Optimization?</h3>
              <p className="text-zinc-500 text-sm mb-5 max-w-md mx-auto">Build your full peptide protocol with PeptIQ — AI-powered, biomarker-driven, personalized to you.</p>
              <Link to="/blogs/article/peptiq-the-ai-powered-app-that-finally-makes-peptide-protocols-simple"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#c8ff00] text-black text-xs font-bold uppercase tracking-wider hover:bg-white transition-colors no-underline"
                data-testid="peptiq-cta">
                Learn About PeptIQ <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
